import { useUpdateUserForm } from "@/features/management-settings/hooks/use-update-user-form";
import { changeLanguage } from "@/i18n/i18n";
import { toAppLanguage } from "@/i18n/to-app-language";
import type { AppLanguage } from "@/i18n/types/app-languages.types";
import { showAlertCallout } from "@/shared/alerts";
import { GetMeResponseDtoConnectionType } from "@/spz-api/model";
import {
  getGetMeQueryKey,
  getGetUserListPaginatedQueryKey,
  useGetMe,
  useUpdateUserNotificationsPreferences,
} from "@/spz-api/user/user";
import type { ValidationError } from "@spz/ui/components/upload-button";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { UserProfileFormValues } from "../management-settings/forms/schemas/user-profile-form.schema";

export const useUserProfileViewModel = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: me } = useGetMe();
  const [preview, setPreview] = useState<string | null>(me?.avatarUrl || null);
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(
    me?.notificationsEnabled ?? false,
  );

  const isUsernamePassword =
    me?.connectionType !== GetMeResponseDtoConnectionType.sso;

  const appLanguageOptions: {
    value: AppLanguage;
    label: string;
  }[] = [
    {
      value: "fr",
      label: t("french"),
    },
    {
      value: "en",
      label: t("english"),
    },
  ];

  const invalidateUserQueries = () => {
    queryClient.invalidateQueries({
      queryKey: getGetMeQueryKey(),
    });
    queryClient.invalidateQueries({
      queryKey: getGetUserListPaginatedQueryKey({}),
    });
  };

  const { form, onSubmit } = useUpdateUserForm({
    onSuccess: async (formData) => {
      try {
        invalidateUserQueries();
        changeLanguage(formData.preferredLanguage);
      } catch (error) {
        showAlertCallout({
          type: "critical",
          title: t("profile_update_error"),
          description: error instanceof Error ? error.message : undefined,
        });
      }
    },
    onError: (error) => {
      showAlertCallout({
        type: "critical",
        title: t("profile_update_error"),
        description: error.message,
      });
    },
  });

  const {
    mutate: updateUserNotificationsPreferences,
    isPending: isNotificationsEnabledLoading,
  } = useUpdateUserNotificationsPreferences({
    mutation: {
      onSuccess: () => {
        showAlertCallout({
          type: "success",
          title: t("notifications_preferences_update_success"),
        });

        invalidateUserQueries();
      },
      onError: () => {
        showAlertCallout({
          type: "critical",
          title: t("notifications_preferences_update_error"),
        });
      },
    },
  });

  const handleNotificationsPreferencesChanges = (checked: boolean) => {
    setNotificationsEnabled(checked);
    updateUserNotificationsPreferences(
      {
        data: {
          notificationsEnabled: checked,
        },
      },
      {
        onError: () => {
          setNotificationsEnabled(me?.notificationsEnabled ?? false);
        },
      },
    );
  };

  useEffect(() => {
    if (me) {
      form.reset({
        firstName: me.firstName ?? "",
        lastName: me.lastName ?? "",
        preferredLanguage: toAppLanguage(me.preferredLanguage),
        deleteAvatar: false,
        avatarUrl: me.avatarUrl ?? null,
      } satisfies UserProfileFormValues);
      setPreview(me.avatarUrl || null);
      setNotificationsEnabled(me.notificationsEnabled ?? false);
    }
  }, [me, form]);

  const handleAvatarUpload = async (file: File) => {
    form.setValue("file", file, { shouldDirty: true });
    form.setValue("deleteAvatar", false);

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    // Trigger autosave to persist the avatar
    await handleAutosave();

    // Cleanup old URL to avoid memory leaks
    return () => URL.revokeObjectURL(objectUrl);
  };

  const handleAvatarValidationError = (error: ValidationError) => {
    showAlertCallout({
      type: "critical",
      title: error.title,
      description: error.description,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: form and onSubmit dependencies would change on every render, causing an infinite loop
  useEffect(() => {
    return () => {
      const isDirty = form.formState.isDirty;
      if (isDirty) {
        form.handleSubmit(async (data) => {
          onSubmit(data).then(() => {
            showAlertCallout({
              type: "success",
              title: t("profile_update_success"),
            });
          });
        })();
      }
    };
  }, []);

  const handleAutosave = async () => {
    const isValid = await form.trigger();
    if (isValid && form.formState.isDirty) {
      await form.handleSubmit(onSubmit)();
    }
  };

  return {
    form,
    me,
    onSubmit: async () => {
      await form.handleSubmit(onSubmit)();
    },
    preview,
    setPreview,
    isUsernamePassword,
    handleAvatarUpload,
    handleAvatarValidationError,
    handleNotificationsPreferencesChanges,
    notificationsEnabled,
    isNotificationsEnabledLoading,
    appLanguageOptions,
    handleAutosave,
  };
};
